import { TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { numberFormatters } from "../../shared/utils/numberFormatters";

type EditableNumericFieldProps = {
    id?: string;
    name?: string;
    label: string;
    value: number | null;
    onValueChange?: (newValue: number) => void | undefined;
    formatter?: (value: number) => string;
    width?: number;
    step?: number | "any";
    disabled?: boolean;
    disabledColour?: string;
};

export function NumericField({
    id,
    name,
    label,
    value,
    onValueChange,
    formatter = (value: number) => numberFormatters.format2(value, { useGroupFormat: false }),
    width = 100,
    step = 0,
    disabled = false,
    disabledColour = "rgb(100,100,100)",
}: EditableNumericFieldProps){
    const defaultValue = 0;
    const defaultValueToDisplay = String(0);
    
    const [input, setInput] = useState(defaultValueToDisplay);

    useEffect(() => {
        setInput(value == null
            ? defaultValueToDisplay
            : formatter(value));
    }, [value]);

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInput(event.target.value);
    };

    const onBlur = () => {
        const newValue = input.trim();
        
        if (newValue === "") {
            onValueChange?.(defaultValue);
            setInput(defaultValueToDisplay);
            return;
        }
        
        const numberValue = Number(newValue);
        
        if (Number.isFinite(numberValue)) {
            onValueChange?.(numberValue);
            setInput(formatter(numberValue));
        } else {
            setInput(value == null
                ? defaultValueToDisplay
                : formatter(value));
        } 
    };

    return(
        <TextField
            margin="normal"
            id={id}
            name={name}
            label={label}
            type="number"
            inputProps={{ step }}
            variant="standard"
            size="small"
            value={input}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            sx={{
                width,
                "& .MuiInputBase-input.Mui-disabled": {
                    color: disabledColour,
                    WebkitTextFillColor: disabledColour, // Chrome requirement
                },
                "& .MuiInputLabel-root.Mui-disabled": {
                    color: disabledColour,
                    opacity: "0.8"
                },
                margin: 0,
                padding: 0
            }}
        />
    )
}
